import { Edit, Plus, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { activiteService } from "../services/api";

const Activites = () => {
  const [activites, setActivites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingActivite, setEditingActivite] = useState(null);
  const [formData, setFormData] = useState({
    nom: "",
    type_activite: "",
    description: "",
    points_attribues: 1,
    date_activite: "",
    duree_minutes: "",
    lieu: "",
  });

  useEffect(() => {
    fetchActivites();
  }, []);

  const fetchActivites = async () => {
    try {
      setLoading(true);
      const response = await activiteService.getActivites();
      setActivites(response.data);
    } catch (error) {
      console.error("Error fetching activities:", error);
      toast.error("Erreur lors du chargement des activités");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingActivite) {
        await activiteService.updateActivite(editingActivite.id, formData);
        toast.success("Activité mise à jour avec succès");
      } else {
        await activiteService.createActivite(formData);
        toast.success("Activité créée avec succès");
      }

      setShowModal(false);
      setEditingActivite(null);
      resetForm();
      fetchActivites();
    } catch (error) {
      console.error("Error saving activity:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleEdit = (activite) => {
    setEditingActivite(activite);
    setFormData({
      nom: activite.nom,
      type_activite: activite.type_activite,
      description: activite.description || "",
      points_attribues: activite.points_attribues,
      date_activite: activite.date_activite
        ? new Date(activite.date_activite).toISOString().slice(0, 16)
        : "",
      duree_minutes: activite.duree_minutes || "",
      lieu: activite.lieu || "",
    });
    setShowModal(true);
  };

  const handleDelete = async (activite) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette activité ?")) {
      try {
        await activiteService.deleteActivite(activite.id);
        toast.success("Activité supprimée avec succès");
        fetchActivites();
      } catch (error) {
        console.error("Error deleting activity:", error);
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  const handleToggleStatus = async (activite) => {
    try {
      await activiteService.toggleStatus(activite.id);
      toast.success(`Activité ${activite.actif ? "désactivée" : "activée"}`);
      fetchActivites();
    } catch (error) {
      console.error("Error toggling status:", error);
      toast.error("Erreur lors du changement de statut");
    }
  };

  const resetForm = () => {
    setFormData({
      nom: "",
      type_activite: "",
      description: "",
      points_attribues: 1,
      date_activite: "",
      duree_minutes: "",
      lieu: "",
    });
  };

  const openModal = () => {
    setEditingActivite(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingActivite(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Gestion des activités
            </h1>
            <p className="text-gray-600">
              Créez et gérez les activités pour les parents
            </p>
          </div>
          <button
            onClick={openModal}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Nouvelle activité</span>
          </button>
        </div>
      </div>

      {/* Activités List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activité
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Points
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activites.map((activite) => (
                <tr key={activite.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {activite.nom}
                    </div>
                    {activite.description && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {activite.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-800">
                      {activite.type_activite}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {activite.date_activite
                      ? new Date(activite.date_activite).toLocaleDateString()
                      : "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {activite.points_attribues}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        activite.actif
                          ? "bg-success-100 text-success-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {activite.actif ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(activite)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(activite)}
                        className="text-warning-600 hover:text-warning-900"
                      >
                        {activite.actif ? (
                          <ToggleLeft className="h-4 w-4" />
                        ) : (
                          <ToggleRight className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(activite)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {editingActivite ? "Modifier l'activité" : "Nouvelle activité"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Nom de l'activité *</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) =>
                      setFormData({ ...formData, nom: e.target.value })
                    }
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="label">Type d'activité *</label>
                  <select
                    value={formData.type_activite}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        type_activite: e.target.value,
                      })
                    }
                    className="input-field"
                    required
                  >
                    <option value="">Sélectionner un type</option>
                    <option value="reunion">Réunion</option>
                    <option value="conference">Conférence</option>
                    <option value="atelier">Atelier</option>
                    <option value="evenement">Événement</option>
                    <option value="formation">Formation</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="input-field"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="label">Points attribués *</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.points_attribues}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        points_attribues: parseInt(e.target.value),
                      })
                    }
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="label">Date et heure</label>
                  <input
                    type="datetime-local"
                    value={formData.date_activite}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        date_activite: e.target.value,
                      })
                    }
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="label">Durée (minutes)</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.duree_minutes}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        duree_minutes: parseInt(e.target.value),
                      })
                    }
                    className="input-field"
                  />
                </div>
              </div>

              <div>
                <label className="label">Lieu</label>
                <input
                  type="text"
                  value={formData.lieu}
                  onChange={(e) =>
                    setFormData({ ...formData, lieu: e.target.value })
                  }
                  className="input-field"
                />
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingActivite ? "Mettre à jour" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Activites;
